/**
 * MSR Portal — Verify a Sale (replaces "Record Sale", business lead 2026-10-27: "we don't need to collect this info").
 * The rep types the member's email; if it belongs to a new membership the rep gets the credit. Works for any sale,
 * at an event or not ("I could go to the bar for lunch and sell someone sitting next to me"). Inside an activity the
 * same box is on the activity's Overview, where the sale also counts toward that activity's close rate.
 *
 * Sales captured offline by the OLD form still sit in this device's queue until sent: they are drained here, once.
 */
import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import MsrTabs from "../../components/msr/MsrTabs";
import VerifySaleForm from "../../components/msr/VerifySaleForm";
import { useToast } from "../../components/ui/Toast";
import { getMyVerifiedSales } from "../../services/msrFeedback.service";
import { getQueue, processQueue } from "../../services/fieldSaleQueue";

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "America/Chicago",
  });

const MsrVerifySale: React.FC = () => {
  const { showToast } = useToast();
  const { data: sales = [], isLoading } = useQuery({
    queryKey: ["msr-my-verified-sales", "all"],
    queryFn: () => getMyVerifiedSales(null),
  });

  // Old offline "Record Sale" captures still on this device: offer to send them (no new ones are created).
  const [queued, setQueued] = useState(0);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => setQueued(getQueue().length), []);

  return (
    <div className="p-4 space-y-4 max-w-xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-900">Verify a Sale</h1>
      <MsrTabs active="field-sale" />
      <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-700">
          Type the email of the member you signed up. If they have a new
          membership, you get credit for the sale. To find them by name, phone
          or promo code, open the activity you sold at.
        </p>
        <VerifySaleForm />
      </div>

      {queued > 0 && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <p>
            {queued} sale{queued === 1 ? "" : "s"} recorded with the old form{" "}
            {queued === 1 ? "is" : "are"} still waiting on this phone.
          </p>
          <button
            type="button"
            disabled={syncing}
            onClick={async () => {
              setSyncing(true);
              try {
                const out = await processQueue({
                  onBusinessFailure: (entry, message) =>
                    showToast("error", `${entry.label}: ${message}`),
                });
                showToast("success", `Sent ${out.succeeded}`);
              } finally {
                setQueued(getQueue().length);
                setSyncing(false);
              }
            }}
            className="min-h-[40px] rounded-md bg-amber-600 px-3 font-semibold text-white disabled:opacity-50"
          >
            {syncing ? "Sending…" : "Send them now"}
          </button>
        </div>
      )}

      <div className="space-y-2">
        <h2 className="text-base font-semibold text-gray-900">
          Your verified sales (last 90 days)
        </h2>
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : sales.length === 0 ? (
          <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
            None yet.
          </p>
        ) : (
          <ul
            className="divide-y divide-gray-100 rounded-md border border-gray-200 bg-white"
            data-testid="msr-my-verified-sales"
          >
            {sales.map((s) => (
              <li
                key={s.id}
                className="flex items-start justify-between gap-2 p-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium text-gray-900">
                    {s.member_email ?? "Verified by name, phone or promo code"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {s.event_title ? s.event_title : "No activity"}
                  </div>
                </div>
                <div className="shrink-0 text-xs text-gray-500">
                  {fmt(s.verified_at)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MsrVerifySale;
